import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "button", 
    "dropdown", 
    "searchInput", 
    "optionsList", 
    "option", 
    "radio", 
    "selectedText", 
    "hiddenInput", 
    "noResults"
  ]

  connect() {
    this.selectedValue = null
    this.isOpen = false
    
    // Initialize selected value from hidden input
    const initialValue = this.hiddenInputTarget.value
    if (initialValue) {
      this.selectedValue = initialValue
    }
    
    // Close dropdown when clicking outside
    this._boundHandleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this._boundHandleClickOutside)
    
    // Trigger calculator state update after initialization
    setTimeout(() => {
      this.updateCalculatorState()
    }, 100)
  }

  disconnect() {
    if (this._boundHandleClickOutside) {
      document.removeEventListener('click', this._boundHandleClickOutside)
      this._boundHandleClickOutside = null
    }
  }

  toggleDropdown(event) {
    event.stopPropagation()
    
    if (this.isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown() {
    this.dropdownTarget.classList.remove('hidden')
    // Check for arrow element more defensively
    const arrowElement = this.element.querySelector('[data-uikit--select-single-target="arrow"]')
    if (arrowElement) {
      arrowElement.style.transform = 'rotate(180deg)'
    }
    this.isOpen = true
    
    // Focus search input when opening
    setTimeout(() => {
      this.searchInputTarget.focus()
    }, 100)
  }

  closeDropdown() {
    this.dropdownTarget.classList.add('hidden')
    // Check for arrow element more defensively
    const arrowElement = this.element.querySelector('[data-uikit--select-single-target="arrow"]')
    if (arrowElement) {
      arrowElement.style.transform = 'rotate(0deg)'
    }
    this.isOpen = false
    
    // Clear search when closing
    this.searchInputTarget.value = ''
    this.filterOptions()
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

  filterOptions() {
    const searchTerm = this.searchInputTarget.value.toLowerCase()
    let visibleCount = 0

    this.optionTargets.forEach((option) => {
      const label = option.dataset.label.toLowerCase()
      const isVisible = label.includes(searchTerm)
      
      option.style.display = isVisible ? 'flex' : 'none'
      if (isVisible) visibleCount++
    })

    // Show/hide no results message
    if (visibleCount === 0 && searchTerm !== '') {
      this.noResultsTarget.classList.remove('hidden')
    } else {
      this.noResultsTarget.classList.add('hidden')
    }
  }

  selectOption(event) {
    let option, radio

    // Check if the clicked element is the radio itself
    if (event.currentTarget.type === 'radio') {
      radio = event.currentTarget
      option = radio.closest('[data-uikit--select-single-target="option"]')
      // Don't stop propagation for radio clicks to allow natural behavior
    } else {
      event.stopPropagation()
      option = event.currentTarget
      radio = option.querySelector('input[type="radio"]')
      // Set radio state manually for non-radio clicks
      radio.checked = true
    }

    const value = option.dataset.value
    const label = option.dataset.label

    // Update selection
    this.selectedValue = value

    // Update display and close dropdown
    this.updateDisplay()
    this.updateHiddenInput()
    this.closeDropdown()
    
    // Update calculator state after selection
    setTimeout(() => {
      this.updateCalculatorState()
    }, 50)
  }

  updateDisplay() {
    const getSelectedTextElement = () => {
      if (this.hasSelectedTextTarget) return this.selectedTextTarget
      return this.element.querySelector('[data-uikit--select-single-target="selectedText"]')
    }

    const selectedTextElement = getSelectedTextElement()
    if (!selectedTextElement) return

    // Read placeholder from component attribute, fallback to default
    const placeholder = this.element.getAttribute('data-uikit--select-single-placeholder-value') || 'Select an option...'

    if (this.selectedValue) {
      const selectedOption = this.optionTargets.find(option => 
        option.dataset.value === this.selectedValue
      )
      
      if (selectedOption) {
        selectedTextElement.textContent = selectedOption.dataset.label
      } else {
        selectedTextElement.innerHTML = `<span class="text-neutral-500">${placeholder}</span>`
      }
    } else {
      selectedTextElement.innerHTML = `<span class="text-neutral-500">${placeholder}</span>`
    }
  }

  updateHiddenInput() {
    this.hiddenInputTarget.value = this.selectedValue || ''

    // Disable the hidden input when there's no selection to prevent empty values from being submitted
    if (!this.selectedValue) {
      this.hiddenInputTarget.disabled = true
    } else {
      this.hiddenInputTarget.disabled = false
    }

    // Trigger change event for form handling
    const event = new Event('change', { bubbles: true, cancelable: true })
    this.hiddenInputTarget.dispatchEvent(event)

    // Also trigger input event for broader compatibility
    const inputEvent = new Event('input', { bubbles: true, cancelable: true })
    this.hiddenInputTarget.dispatchEvent(inputEvent)

    // For compatibility with existing calculator logic, also trigger a custom event
    const customEvent = new CustomEvent('securitySelected', { 
      detail: { value: this.selectedValue },
      bubbles: true 
    })
    this.hiddenInputTarget.dispatchEvent(customEvent)
  }

  updateCalculatorState() {
    // Trigger calculator state update if calculator controller exists
    if (window.calculatorController && typeof window.calculatorController.updateCalculateButtonState === 'function') {
      window.calculatorController.updateCalculateButtonState()
    }
  }

  // Public method to get selected value
  getSelectedValue() {
    return this.selectedValue
  }

  // Public method to set selected value
  setSelectedValue(value) {
    this.selectedValue = value
    
    // Update radio buttons
    this.radioTargets.forEach(radio => {
      const option = radio.closest('[data-uikit--select-single-target="option"]')
      const optionValue = option.dataset.value
      radio.checked = optionValue === value
    })
    
    this.updateDisplay()
    this.updateHiddenInput()
    
    // Update calculator state after setting value
    setTimeout(() => {
      this.updateCalculatorState()
    }, 50)
  }

  // Public method to clear selection
  clear() {
    this.selectedValue = null
    this.radioTargets.forEach(radio => radio.checked = false)
    this.updateDisplay()
    this.updateHiddenInput()
    
    // Update calculator state after clearing
    setTimeout(() => {
      this.updateCalculatorState()
    }, 50)
  }

  // Prevent event propagation when clicking radio directly
  preventPropagation(event) {
    event.stopPropagation()
  }
} 
